import React, { useRef, useState } from "react";
import {
	CCButton,
	CCDateField,
	CCField,
	CCSpinner,
	CCSwitch,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { toDatePart, toIso, toTimePart } from "../../utils/eventDates";
import { useBanner } from "../../hooks/useBanner";
import {
	ACCEPTED_IMAGE_TYPES,
	BannerImageError,
	bannerDataUrl,
} from "../../utils/bannerImage";
import { EventBanner, EventDoc } from "../../types/event";
import { EventBumpTime } from "./EventBumpTimes";
import { EventDocuments } from "./EventDocuments";
import { EventPublicToggle } from "./EventPublicToggle";
import "./eventManager.css";

interface EventSidebarProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
	/** Passed down to the sign-off panel — see EventSignOffPanel. */
	canWithdraw?: boolean;
}

/* CCDateField and CCField type="time" are separate controls, so the single
   datetime-local input this replaced is now split into a date part and a time
   part. The stored value stays one ISO instant either way. The quick-create
   dialog does the same, so the helpers are shared. */

/**
 * Right-hand sidebar — pymble-swipe's RightSideData concept: start/end dates
 * with a "Find a date" shortcut to the calendar, an add-start/end-time toggle,
 * the featured image, the sign-off state, then Save.
 *
 * The featured image is v2's Banner tab promoted into the sidebar, which is how
 * pymble-swipe frames it. Banners come from `event/admin/banners/`; pymble-swipe
 * offers a file upload, but the S2 API only exposes a fixed banner library, so
 * this is a picker rather than an uploader.
 *
 * TWO SECTIONS, not one stack. Everything above `Save changes` is an edit to
 * this event, and `Save changes` closes that section; sign-off is a separate
 * section below it, because it is not an edit at all — it is where the event
 * stands with other people, and `Submit for sign-off` sends it to them.
 *
 * They were adjacent buttons in one column before, which put the safest action
 * in the editor next to the one that puts an event in front of approvers, with
 * nothing between them but a gap. The rule and the section headings say which
 * half of the sidebar you are in.
 *
 * TWO CARDS, sharing one height. `--em-editor-max` is set on the column wrapper,
 * and it caps each panel that reads it INDEPENDENTLY — so two cards each reading
 * it would each claim the whole column and grow the page, which is why this was
 * one card for a while. The wrapper is now the flex column that carries the cap
 * (`.em-sidebar-stack`): the edit card takes the room left over and scrolls
 * inside itself, and the sign-off card keeps its natural height beneath it.
 */
export const EventSidebar: React.FC<EventSidebarProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	const { banners, bannersLoaded } = useBanner();
	const [showTimes, setShowTimes] = useState<boolean>(() => {
		// Default the toggle on when either end of the event carries a real time.
		const start = event.StartDateTime ? new Date(event.StartDateTime) : null;
		const end = event.EndDateTime ? new Date(event.EndDateTime) : null;
		const hasTime = (d: Date | null) =>
			!!d &&
			!Number.isNaN(d.getTime()) &&
			(d.getHours() !== 0 || d.getMinutes() !== 0);
		return hasTime(start) || hasTime(end);
	});
	const [showBannerPicker, setShowBannerPicker] = useState(false);
	const toast = useToast();
	/** Reading, decoding and re-encoding the chosen file. Brief, but not free. */
	const [preparing, setPreparing] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);

	/**
	 * A picked file becomes the event's banner.
	 *
	 * The data URI is written straight into the draft and travels with the next
	 * save: the API lifts it into blob storage and hands back a reference, so
	 * there is no upload call and nothing to clean up if the person never saves.
	 * See utils/bannerImage.
	 *
	 * `_id` is deliberately dropped rather than carried over. It names an entry
	 * in the shared library, and this image is not in it — leaving the old id
	 * beside a new URL would make the picker highlight a tile that is no longer
	 * what the event is using.
	 */
	const onPickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		/* Cleared immediately, so choosing the SAME file again still fires a
		   change event. Without this a failed attempt cannot be retried. */
		event.target.value = "";
		if (!file) return;
		setPreparing(true);
		try {
			onChange({ Banner: { ImageUrl: await bannerDataUrl(file) } });
			setShowBannerPicker(false);
		} catch (error) {
			toast.show({
				message:
					error instanceof BannerImageError
						? error.message
						: "That image could not be used. Please try another.",
				severity: "warning",
				duration: 5000,
			});
		} finally {
			setPreparing(false);
		}
	};

	const banner: EventBanner = event.Banner ?? {};

	const startDate = toDatePart(event.StartDateTime);
	const startTime = toTimePart(event.StartDateTime);
	const endDate = toDatePart(event.EndDateTime);
	const endTime = toTimePart(event.EndDateTime);

	/**
	 * Whether the event ends before it starts.
	 *
	 * The test differs by mode, and getting that wrong is the trap here. With
	 * times off, both instants are midnight, so a perfectly ordinary one-day
	 * event has end EQUAL to start — comparing instants would flag every
	 * single-day event as broken. Date-only therefore compares days and allows
	 * the same day; only a timed event requires the end to be strictly later.
	 *
	 * A half-filled range is not an error. Someone mid-edit with no end date yet
	 * has not done anything wrong, and shouting at them for it is noise.
	 */
	const rangeInvalid = (() => {
		if (!startDate || !endDate) return false;
		if (!showTimes) return endDate < startDate;
		const from = new Date(event.StartDateTime ?? "");
		const to = new Date(event.EndDateTime ?? "");
		if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()))
			return false;
		return to.getTime() <= from.getTime();
	})();

	/**
	 * Move the start past the end and the end comes with it, as a calendar does.
	 *
	 * Without this, dragging the start forward silently leaves the event ending
	 * before it begins and the only way out is to notice and fix the end too.
	 * Only the day is carried — the end keeps its own time, so a 9am–3pm event
	 * moved to next week is still 9am–3pm.
	 */
	const onStartDate = (value: string) => {
		const patch: Partial<EventDoc> = {
			StartDateTime: toIso(value, showTimes ? startTime : ""),
		};
		// Lexical comparison is safe on `YYYY-MM-DD`.
		if (value && endDate && endDate < value) {
			patch.EndDateTime = toIso(value, showTimes ? endTime : "");
		}
		onChange(patch);
	};

	return (
		<>
			<div className="em-panel em-sidebar" data-surface="card">
				<div className="em-field">
					<div className="em-date-time">
						<CCDateField
							id="em-start"
							label="Start date"
							fullWidth
							value={startDate}
							disabled={readOnly}
							onChange={(value) => onStartDate(String(value))}
						/>
						{showTimes && (
							<CCField
								id="em-start-time"
								label="Start time"
								type="time"
								fullWidth
								className="em-time-field"
								value={startTime}
								disabled={readOnly}
								onChange={(e) =>
									onChange({
										StartDateTime: toIso(startDate, e.target.value),
									})
								}
							/>
						)}
					</div>
				</div>

				<div className="em-field">
					{/* The picker will not offer a day before the start. CCDateField
					    brands its values as IsoDate; the app carries plain
					    `YYYY-MM-DD`, so the cast is confined to this boundary. */}
					<div className="em-date-time">
						<CCDateField
							id="em-end"
							label="End date"
							fullWidth
							value={endDate}
							min={(startDate || undefined) as never}
							disabled={readOnly}
							error={
								rangeInvalid
									? showTimes
										? "The event must end after it starts."
										: "The end date cannot be before the start date."
									: undefined
							}
							onChange={(value) =>
								onChange({
									EndDateTime: toIso(String(value), showTimes ? endTime : ""),
								})
							}
						/>
						{showTimes && (
							<CCField
								id="em-end-time"
								label="End time"
								type="time"
								fullWidth
								className="em-time-field"
								value={endTime}
								disabled={readOnly}
								onChange={(e) =>
									onChange({
										EndDateTime: toIso(endDate, e.target.value),
									})
								}
							/>
						)}
					</div>
				</div>

				{/* Bump in and out on one line. NOT gated on `showTimes`: a
				    whole-day excursion still has a crew arriving at seven, and
				    hiding the call time behind a switch about something else is
				    how it would never be found. */}
				<div className="em-field em-bump-row">
					<EventBumpTime
						which="in"
						event={event}
						readOnly={readOnly}
						onChange={onChange}
					/>
					<EventBumpTime
						which="out"
						event={event}
						readOnly={readOnly}
						onChange={onChange}
					/>
				</div>

				<div className="em-field em-switch-row">
					<CCSwitch
						id="em-show-times"
						label="Add start/end time"
						checked={showTimes}
						disabled={readOnly}
						onChange={setShowTimes}
					/>
					<EventPublicToggle
						event={event}
						readOnly={readOnly}
						onChange={onChange}
					/>
				</div>

				{/* The whole section goes when there is nothing to show AND nothing
				    to press. A read-only event with no banner would otherwise render
				    a heading over blank space for ever. */}
				{(banner.ImageUrl || !readOnly) && (
					<div className="em-field">
						<label className="em-label">Featured image</label>
						{/* COLLAPSED WHEN EMPTY. The preview is a square, so on a
					    sidebar it is around 280px of dashed box saying "No image
					    set" — a quarter of the panel spent on the absence of a
					    decorative image, pushing the dates and Save further down on
					    every event that has none, which is most of them. The button
					    below already says "Choose image", so nothing is lost.

					    An empty section for a READ-ONLY viewer disappears entirely:
					    there is nothing to show and nothing to press, so a label
					    over blank space is the only thing left to render. */}
						{banner.ImageUrl && (
							<div className="em-banner-preview">
								{/* On the image rather than under it. A Remove button in the row
						    below sat beside Change image as an equal, which it is not:
						    one is the ordinary action and the other undoes a choice.

						    Unconfirmed, because nothing is written until Save, so this
						    costs at most one click of Discard. That is the opposite of
						    the attendee removal, which reaches the API the moment it is
						    chosen and therefore asks first. */}
								{banner.ImageUrl && !readOnly && (
									<button
										type="button"
										className="em-banner-clear"
										title="Remove image"
										aria-label="Remove image"
										onClick={() => {
											/* An EMPTY Banner, not a missing one. The editor sends
									   the whole draft and the API writes `Banner._id` and
									   `Banner.ImageUrl` on every update, so omitting the
									   field would leave the old image in place; `{}`
									   resolves to two nulls, which is what "no image" is
									   stored as. */
											onChange({ Banner: {} });
											setShowBannerPicker(false);
										}}
									>
										<FontAwesomeIcon icon={faXmark} aria-hidden="true" />
									</button>
								)}
								<img src={banner.ImageUrl} alt="Event banner" />
							</div>
						)}

						{!readOnly && (
							<CCButton
								variant="outline"
								size="sm"
								fullWidth
								className="em-mt-sm"
								onClick={() => setShowBannerPicker((v) => !v)}
							>
								{showBannerPicker
									? "Close"
									: banner.ImageUrl
										? "Change image"
										: "Choose image"}
							</CCButton>
						)}

						{showBannerPicker && !readOnly && (
							<>
								<div className="em-banner-upload">
									<CCButton
										variant="outline"
										size="sm"
										fullWidth
										disabled={preparing}
										onClick={() => fileRef.current?.click()}
									>
										{preparing ? "Preparing..." : "Upload an image"}
									</CCButton>
									{/* Hidden and driven by the button: a bare file input cannot
								    be styled to match the kit, and its "No file chosen" text
								    describes a state this flow never sits in — the file is
								    used the moment it is picked. */}
									<input
										ref={fileRef}
										type="file"
										accept={ACCEPTED_IMAGE_TYPES}
										hidden
										onChange={onPickFile}
									/>
									<span className="em-banner-upload__note">
										JPEG, PNG or WebP. Large images are resized, and the image
										is saved with this event rather than added to the library
										below.
									</span>
								</div>

								{/* A SPAN, not a `<label>`. The picker is a group of buttons,
							    and a label belongs to a form control — pointed at a grid
							    it names nothing and clicking it focuses nothing. It gets
							    the label's styling and is tied to the grid by
							    `aria-labelledby` instead, which is the part that
							    actually reaches a screen reader. */}
								<span className="em-label em-mt-sm" id="em-banner-picker-label">
									Select image
								</span>
								{!bannersLoaded ? (
									<div className="em-text-center em-mt-sm">
										<CCSpinner size="sm" />
									</div>
								) : banners.length === 0 ? (
									/* The library is a fixed set served by
								   `event/admin/banners/`. If it comes back empty there
								   is nothing to pick, so say so rather than opening an
								   empty box. */
									<div className="em-picker-empty">
										No event images are available to choose from.
									</div>
								) : (
									<div
										className="em-banner-grid"
										role="group"
										aria-labelledby="em-banner-picker-label"
									>
										{/* `b.id` / `b.img`, NOT `_id` / `ImageUrl`. The library
									    endpoint and the field it is chosen into are two
									    different shapes — see BannerOption. Reading the
									    stored names off the list response gave every tile
									    `src={undefined}`, so the grid was a set of blanks,
									    and choosing one wrote a banner of two undefineds. */}
										{banners.map((b, index) => {
											const chosen = b.id === banner._id;
											return (
												/* A button, not a bare `<img onClick>`. These are
											   controls: without this the picker could not be
											   reached by keyboard at all, and nothing announced
											   which image was in use. */
												<button
													type="button"
													key={b.id}
													className={
														chosen
															? "em-banner-choice em-banner-selected"
															: "em-banner-choice"
													}
													aria-pressed={chosen}
													aria-label={`Event image ${index + 1}`}
													onClick={() => {
														onChange({
															Banner: { _id: b.id, ImageUrl: b.img },
														});
														setShowBannerPicker(false);
													}}
												>
													<img src={b.img} alt="" />
												</button>
											);
										})}
									</div>
								)}
							</>
						)}
					</div>
				)}

				{/* v2's "Activate Permission" switch used to sit here. It is gone:
				    releasing an event is what the sign-off chain below decides, and a
				    switch that could put an event in front of carers without it left
				    two answers to one question.

				    `ActiveAt` — the field it wrote, which the API turns into
				    `Approval.ShowPermission` — is NOT dropped from the payload. The
				    editor PUTs the whole draft, so whatever an event already carries
				    round-trips untouched. See the note on `moveEvent` in
				    useEventManager: the API writes `ActiveAt` on every update, so a
				    partial payload would blank it and quietly withdraw the permission
				    from carers. */}
			</div>


			{/* Attached documents. Was the Files tab; a sidebar card of its own,
			    below the sign-off, so it is on screen whichever tab is open. The
			    upload is addressed to the stored event, hence `_id`. */}
			<div className="em-panel em-files-card" data-surface="card">
				<EventDocuments
					eventId={event._id}
					event={event}
					readOnly={readOnly}
					onChange={onChange}
				/>
			</div>
		</>
	);
};
